/* FABLEX V14 — UI behaviour only. Does not alter Shopify's media selection,
   product-form, variant, cart or accelerated-checkout handlers. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function updateRailArrows(section) {
    const rail = section.querySelector('[data-fv13-track]');
    const prev = section.querySelector('[data-fv13-prev]');
    const next = section.querySelector('[data-fv13-next]');
    if (!rail || !prev || !next) return;
    const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
    prev.disabled = rail.scrollLeft <= 3;
    next.disabled = rail.scrollLeft >= max - 3;
  }

  function initializeCreators(section) {
    if (section.dataset.fv14Ready === '1') return;
    section.dataset.fv14Ready = '1';
    const rail = section.querySelector('[data-fv13-track]');
    if (!rail) return;
    const previews = [...rail.querySelectorAll('video')];
    const dialog = section.querySelector('[data-fv14-dialog]');
    const fullVideo = dialog?.querySelector('[data-fv14-player]');
    const visible = new Set();
    let sectionOnscreen = true;
    let returnFocusTo = null;

    previews.forEach(video => {
      video.addEventListener('playing', () => {
        video.closest('.fv13-creators__card')?.classList.add('is-started');
      }, {once:true});
    });

    const pausePreviews = () => previews.forEach(video => video.pause());
    const playPreview = video => {
      if (!video || reducedMotion() || document.hidden || !sectionOnscreen || dialog?.open || !visible.has(video)) return;
      video.muted = true;
      video.playsInline = true;
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    };
    const playVisible = () => visible.forEach(playPreview);

    // Play only while the rail is actually within the window viewport.
    if ('IntersectionObserver' in window && previews.length) {
      new IntersectionObserver(entries => {
        sectionOnscreen = entries.some(entry => entry.isIntersecting);
        if (sectionOnscreen) playVisible();
        else pausePreviews();
      }, {threshold:0}).observe(section);
    }

    // Autoplay only MUTED clips which are actually visible within the rail.
    if ('IntersectionObserver' in window && previews.length) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= .45) {
            visible.add(video);
            playPreview(video);
          } else {
            visible.delete(video);
            video.pause();
          }
        });
      }, { root: rail, threshold: [0, .45, .65] });
      previews.forEach(video => observer.observe(video));
    } else {
      // Browser fallback: poster thumbnails are still clickable even if
      // automatic in-view preview playback isn't supported.
      previews.forEach(video => visible.add(video));
      playVisible();
    }

    const step = direction => {
      const card = rail.querySelector('.fv13-creators__card');
      const width = card?.getBoundingClientRect().width || rail.clientWidth * .42;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 11;
      rail.scrollBy({left: direction * (width + gap), behavior: reducedMotion() ? 'instant' : 'smooth'});
    };
    section.querySelector('[data-fv13-prev]')?.addEventListener('click', () => step(-1));
    section.querySelector('[data-fv13-next]')?.addEventListener('click', () => step(1));
    rail.addEventListener('scroll', () => updateRailArrows(section), {passive:true});
    if ('ResizeObserver' in window) new ResizeObserver(() => updateRailArrows(section)).observe(rail);
    updateRailArrows(section);

    function closeCreator() {
      if (!dialog || !fullVideo) return;
      if (dialog.open) dialog.close();
    }
    function openCreator(button) {
      if (!dialog || !fullVideo || typeof dialog.showModal !== 'function') return;
      const preview = button.closest('.fv13-creators__card')?.querySelector('video');
      if (!preview) return;
      pausePreviews();
      returnFocusTo = button;
      fullVideo.pause();
      fullVideo.removeAttribute('src');
      fullVideo.querySelectorAll('source').forEach(source => source.remove());
      const sources = preview.querySelectorAll('source');
      if (sources.length) {
        sources.forEach(source => fullVideo.appendChild(source.cloneNode(true)));
      } else {
        const url = preview.currentSrc || preview.getAttribute('src');
        if (!url) return;
        fullVideo.src = url;
      }
      if (preview.poster) fullVideo.poster = preview.poster;
      else fullVideo.removeAttribute('poster');
      fullVideo.muted = false;
      fullVideo.volume = 1;
      fullVideo.currentTime = 0;
      fullVideo.load();
      dialog.showModal();
      const result = fullVideo.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
    rail.querySelectorAll('[data-fv14-expand]').forEach(button => {
      button.addEventListener('click', () => openCreator(button));
    });
    dialog?.querySelector('[data-fv14-close]')?.addEventListener('click', closeCreator);
    dialog?.addEventListener('click', event => { if (event.target === dialog) closeCreator(); });
    dialog?.addEventListener('close', () => {
      if (fullVideo) {
        fullVideo.pause();
        fullVideo.removeAttribute('src');
        fullVideo.querySelectorAll('source').forEach(source => source.remove());
        fullVideo.load();
      }
      playVisible();
      if (returnFocusTo?.isConnected) returnFocusTo.focus({preventScroll:true});
      returnFocusTo = null;
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pausePreviews();
      else playVisible();
    });
  }

  function initializeGallery(gallery) {
    if (gallery.dataset.fv14GalleryReady === '1') return;
    const guide = gallery.querySelector('[data-fv14-gallery-guide]');
    if (!guide) return;
    gallery.dataset.fv14GalleryReady = '1';
    const current = guide.querySelector('[data-fv14-slide-current]');
    gallery.addEventListener('slideshow:select', event => {
      // Use native Shopify's slideshow event; do not duplicate slide handling.
      if (!event.target.matches?.('slideshow-component')) return;
      const index = Number(event.detail?.index);
      if (current && Number.isFinite(index)) current.textContent = String(index + 1);
      if (event.detail?.userInitiated) guide.classList.add('is-explored');
    });
  }

  // Product gallery videos are deliberately muted at first (autoplay policy).
  // Allow sound by an explicit user gesture. This is separate from creator reels.
  function setGallerySoundButton(button, video) {
    if (!button || !video) return;
    const on = !video.muted && video.volume > 0;
    button.dataset.soundOn = String(on);
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute('aria-label', on ? 'Mute product video' : 'Turn sound on for product video');
    const label = button.querySelector('[data-fv15-sound-label]');
    if (label) label.textContent = on ? 'Sound off' : 'Sound on';
  }

  document.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-fv15-gallery-sound]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const media = button.closest('.product-media');
    const deferred = media?.querySelector('deferred-media');
    if (!deferred) return;
    // A Shopify product video first shows a poster. Reveal it before unmuting.
    if (!deferred.hasAttribute('data-media-loaded')) {
      const poster = deferred.querySelector('[ref="deferredMediaPlayButton"]');
      if (poster instanceof HTMLButtonElement) poster.click();
    }
    const videos = [...deferred.querySelectorAll('video')];
    const visibleVideo = videos[videos.length - 1];
    if (!visibleVideo) return;
    const turnSoundOn = visibleVideo.muted || visibleVideo.volume === 0;
    // Shopify's deferred media can contain a cached video and a playing clone.
    for (const video of videos) {
      // Some Horizon builds keep a hidden source video behind the active clone.
      // Never unmute that hidden duplicate (two audio tracks could play at once).
      video.muted = video !== visibleVideo || !turnSoundOn;
      if (video === visibleVideo && turnSoundOn) video.volume = 1;
    }
    setGallerySoundButton(button, visibleVideo);
    if (turnSoundOn && visibleVideo.paused) {
      const promise = visibleVideo.play();
      if (promise?.catch) promise.catch(() => {});
    }
  });

  // Keep our sound button in sync if a customer uses the browser's video UI.
  document.addEventListener('volumechange', event => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const button = video.closest('.product-media')?.querySelector('[data-fv15-gallery-sound]');
    if (!button) return;
    const videos = video.closest('deferred-media')?.querySelectorAll('video');
    if (videos?.length && videos[videos.length - 1] !== video) return;
    setGallerySoundButton(button, video);
  }, true);

  function boot(root=document) {
    root.querySelectorAll('[data-fv13-creators]').forEach(initializeCreators);
    root.querySelectorAll('media-gallery').forEach(initializeGallery);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));
})();
